import { AiOutlineQuestionCircle } from "@react-icons/all-files/ai/AiOutlineQuestionCircle";
import { FiCheck } from "@react-icons/all-files/fi/FiCheck";
import { FiEdit3 } from "@react-icons/all-files/fi/FiEdit3";
import { FiLoader } from "@react-icons/all-files/fi/FiLoader";
import { FiPrinter } from "@react-icons/all-files/fi/FiPrinter";
import { FiZap } from "@react-icons/all-files/fi/FiZap";
import { Modal, Popover } from "antd";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as GPSInsightsActions from "../../../../Actions/GPSInsightsActions";
import {
    resetGpsInsightResults,
    updateJobTitle
} from "../../../../Actions/GPSInsightsActions";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { ReportTypeEnum } from "../../../../Enums/ReportTypeEnum";
import { getAllReportsLink } from "../../../../Logic/ReportLogic";
import { IGCPBigQueryJob, IUser } from "../../../../Models/Models";
import { gpsPrintReadySelector } from "../../../../Reducers/Selectors/GPSInsightsSelectors";
import { userSelector } from "../../../../Reducers/Selectors/UserSelectors";
import {
    ReportActionButton,
    ReportH4,
    ReportHeaderInnerWrap,
    ReportHeaderWrap,
    ReportSharingMenu
} from "../../../Common/";
import { HideUpToTabletXtraLarge } from "../../../Common/DigitalShare/DigitalShareCommonStyles";
import EditInput from "../../../Common/EditInput";
import { ReportBackLink } from "../../../Common/ReportComponents/ReportBackLink";
import {
    DateVerticalBorder,
    GPSHeaderButtonWrap,
    GPSReportHeaderContainer,
    GPSReportHeaderContentContainer,
    LogoVerticalBorder
} from "../Styled";
import { GPSHeaderDateBlock } from "./GPSHeaderDateBlock";

interface GPSHeaderProps {
    job: IGCPBigQueryJob[];
    isSharing: boolean;
    loadingSaveToScout: boolean;
    onPrintClick: () => void;
    onShareClick: () => void;
}

const GPSHeader: FunctionComponent<GPSHeaderProps> = (
    props: GPSHeaderProps
) => {
    const { job, isSharing } = props;
    const dispatch = useDispatch();
    const [editing, setEditing] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [printReady, setPrintReady] = useState<boolean>(false);

    const [viewVideo, setViewVideo] = useState<boolean>(false);
    const [newReportName, setNewReportName] = useState<string>(
        props.job[0].title
    );
    const iconFill = "#515151";

    const printReadyCountdown = useSelector(gpsPrintReadySelector);
    const user: IUser = useSelector(userSelector);
    const userID = user?.userID;

    /*
    Master control switch for removing the print button/
    If true, do not render under any circumstance.
    If false, fall back to any other logic controlling its render
    */
    const masterRemovePrintButton = false;

    useEffect(() => {
        viewVideo ? videoPlayStop("play") : videoPlayStop("stop");
    }, [viewVideo]);

    useEffect(() => {
        let timeout = null;

        if (printReadyCountdown && !printReady) {
            timeout = setTimeout(() => {
                setPrintReady(true);
            }, 3000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [printReadyCountdown]);

    const videoPlayStop = (action: "play" | "stop") => {
        const video = document.getElementById(
            "RetailMatchVideo"
        ) as HTMLVideoElement;
        if (video) {
            switch (action) {
                case "play":
                    video.play();
                    break;
                case "stop":
                default:
                    video.pause();
                    video.currentTime = 0;
                    break;
            }
        }
    };

    const onEditClick = () => {
        setEditing(true);
    };

    const onCancelEditClick = () => {
        setNewReportName(props.job[0].title);
        setEditing(false);
    };

    const onNewReportName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewReportName(event.target.value);
    };

    const onSubmit = () => {
        setLoading(true);
        updateJobTitle(props.job[0].jobId, newReportName).then(() => {
            // Refresh left nav to get new name
            if ((window as any).scout) {
                (window as any).scout.interop.api.refreshMobilyticsVisitorsList();
            }
            setEditing(false);
            setLoading(false);
        });
    };

    const standardReportNameDisplay = (
        <>
            {newReportName}
            {!isSharing && (
                <Popover content="Edit report name">
                    <FiEdit3
                        onClick={onEditClick}
                        style={{
                            position: "relative",
                            top: "2px",
                            marginLeft: "4px",
                            cursor: "pointer",
                            fontSize: 13,
                        }}
                    />
                </Popover>
            )}
        </>
    );

    const handleViewAll = (): void => {
        resetGpsInsightResults();
        const link = getAllReportsLink(user);
        window.location.href = link;
    };

    const getSaveToScoutIcon = (): JSX.Element => {
        const iconSize = "18px";
        if (job[0].viewInScout) {
            return <FiCheck size={iconSize} stroke={iconFill} />;
        } else if (props.loadingSaveToScout) {
            return <FiLoader size={iconSize} stroke={iconFill} />;
        }

        return <FiZap size={iconSize} stroke={iconFill} />;
    };

    const getSaveToScoutTitle = (): string => {
        let verbiage = "";
        if (job[0].viewInScout) {
            verbiage = "Saved to Scout";
        } else if (props.loadingSaveToScout) {
            verbiage = "Loading";
        } else {
            verbiage = "Save to Scout";
        }
        return verbiage;
    };

    return (
        <ReportHeaderWrap>
            <ReportHeaderInnerWrap>
                <ReportBackLink
                    text="All Reports"
                    hide={isSharing}
                    onClick={handleViewAll}
                />
                <GPSReportHeaderContainer>
                    <img
                        src="/Images/MobileInsights/BuxtonMobilytics.svg"
                        style={{ marginBottom: "13px" }}
                    />
                    <LogoVerticalBorder />
                    <GPSReportHeaderContentContainer>
                        <div style={{ marginRight: "15px" }}>
                            <ReportH4 style={{ marginTop: 0, fontSize: "15px" }}>
                                {editing ? (
                                    <EditInput
                                        isLoading={loading}
                                        onSubmit={onSubmit}
                                        onCancel={onCancelEditClick}
                                        name={newReportName}
                                        onChange={onNewReportName}
                                    />
                                ) : (
                                    standardReportNameDisplay
                                )}
                            </ReportH4>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <GPSHeaderDateBlock
                                    job={job[0]}
                                    jobIndex={0}
                                    isSharing={props.isSharing}
                                />
                                {job[1] && (
                                    <>
                                        <DateVerticalBorder />
                                        <GPSHeaderDateBlock
                                            job={job[1]}
                                            jobIndex={1}
                                            isSharing={props.isSharing}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <GPSHeaderButtonWrap className={isSharing ? "sharing" : null}>
                            <ReportActionButton
                                title="Learn About Mobilytics"
                                hideTitle={true}
                                icon={<AiOutlineQuestionCircle fill={iconFill} size="22px" />}
                                noPadding={true}
                                style={{ marginRight: "8px", width: "38px" }}
                                clickCallback={() => {
                                    setViewVideo(true);
                                }}
                            />
                            <Modal
                                title=""
                                visible={viewVideo}
                                onCancel={() => {
                                    setViewVideo(false);
                                }}
                                footer={null}
                                width={700}
                            >
                                <video
                                    id="RetailMatchVideo"
                                    width="100%"
                                    autoPlay={true}
                                    style={{ marginTop: "20px" }}
                                >
                                    <source
                                        src="https://cdn.platform.buxtonco.com/videos/platform/scout/mobilytics-final-2.mp4"
                                        type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            </Modal>
                            {!isSharing ? (
                                <>
                                    <ReportActionButton
                                        title={getSaveToScoutTitle()}
                                        hideTitle={true}
                                        clickCallback={() => {
                                            if (!job[0].viewInScout) {
                                                dispatch(GPSInsightsActions.saveToScout(job[0]));
                                            }
                                        }}
                                        color="black"
                                        disabled={job[0].viewInScout || job[0].uid !== userID}
                                        icon={getSaveToScoutIcon()}
                                        style={{ marginRight: "8px", width: "38px" }}
                                        noPadding={true}
                                    />
                                    <ReportSharingMenu
                                        hideButtonText={true}
                                        jobId={job[0].jobId}
                                        title={job[0].title}
                                        reportType={ReportTypeEnum.Mobilytics}
                                        buttonStyle={{ padding: "0 8.5px" }}
                                        onShareClick={props.onShareClick}
                                        multiBrandOverview={false}
                                    />
                                </>
                            ) : null}
                            {featureFlags.DigitalShare && !isSharing && !masterRemovePrintButton && (
                                <Popover
                                    content={
                                        printReady ? (
                                            <span>
                                                PDF reports will open in a new tab or window.
                                                <br />
                                                If you have a pop-up blocker installed, you may need to
                                                add this site to your whitelist/exclusions.
                                            </span>
                                        ) : (
                                            <span>Report loading, please wait</span>
                                        )
                                    }
                                    trigger="hover"
                                    placement="top"
                                >
                                    <HideUpToTabletXtraLarge>
                                        <ReportActionButton
                                            title="Print"
                                            hideTitle={true}
                                            icon={<FiPrinter />}
                                            noPadding={true}
                                            style={{ marginRight: "8px", width: "38px" }}
                                            clickCallback={() => {
                                                props.onPrintClick();
                                            }}
                                            disabled={!printReady}
                                        />
                                    </HideUpToTabletXtraLarge>
                                </Popover>
                            )}
                        </GPSHeaderButtonWrap>
                    </GPSReportHeaderContentContainer>
                </GPSReportHeaderContainer>
            </ReportHeaderInnerWrap>
        </ReportHeaderWrap>
    );
};

export default GPSHeader;
